/**
 * TurboSheet Pro — Offline License Key System
 *
 * Key format (new): TURBO-XXXXX  (10 chars total: TURBO + hyphen + 5 alphanumeric)
 *   The 5-char suffix encodes a lookup index; full license data is stored in Supabase/localStorage.
 *   For offline-only use, data is embedded and the key acts as a compact token.
 *
 * Legacy formats (TURBO-XXXX-XXXX-XXXX and base64) are still accepted for backward compatibility.
 */

const SECRET = 'TSP_OFFLINE_2026_SECRET_KEY_v1';

// ─── Alphabet for short keys ──────────────────────────────────────────────────
const ALPHA = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // 32 chars, no 0/O/1/I confusion

// ─── Blacklist ────────────────────────────────────────────────────────────────
export const BLACKLISTED_KEYS: string[] = [
  // 'TURBO-XXXXX',
];

// ─── Types ────────────────────────────────────────────────────────────────────
export interface LicenseData {
  clientName: string;
  expiryDate: string;
  maxUploads: number;
  dailyLimit: number;
  modules: string[];
  uid: string;
  rawKey: string;
}

export interface LicenseValidationResult {
  valid: boolean;
  data?: LicenseData;
  error?: string;
}

export interface UsageData {
  totalUploads: number;
  dailyUploads: number;
  lastUploadDate: string;
}

// ─── Simple checksum ─────────────────────────────────────────────────────────
function computeChecksum(parts: string[]): string {
  const str = parts.join('|') + SECRET;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(36).toUpperCase().padStart(8, '0');
}

// ─── Encode payload into base32-like string ───────────────────────────────────
function encodePayload(payload: string): string {
  let num = 0n;
  for (let i = 0; i < payload.length; i++) {
    num = num * 256n + BigInt(payload.charCodeAt(i));
  }
  const base = BigInt(ALPHA.length);
  const chars: string[] = [];
  while (num > 0n) {
    chars.unshift(ALPHA[Number(num % base)]);
    num = num / base;
  }
  return chars.join('') || ALPHA[0];
}

function decodePayload(encoded: string): string {
  const base = BigInt(ALPHA.length);
  let num = 0n;
  for (const ch of encoded) {
    const idx = ALPHA.indexOf(ch);
    if (idx === -1) throw new Error('Invalid character: ' + ch);
    num = num * base + BigInt(idx);
  }
  const bytes: number[] = [];
  while (num > 0n) {
    bytes.unshift(Number(num % 256n));
    num = num / 256n;
  }
  return bytes.map(b => String.fromCharCode(b)).join('');
}

// ─── Generate a random 5-char suffix from ALPHA ───────────────────────────────
function randomSuffix(length: number): string {
  let result = '';
  for (let i = 0; i < length; i++) {
    result += ALPHA[Math.floor(Math.random() * ALPHA.length)];
  }
  return result;
}

// ─── In-memory store for short key → license data mapping ────────────────────
// In a real deployment this would be persisted to Supabase; here we use localStorage.
const SHORT_KEY_STORE_KEY = 'tsp_short_key_store';

function saveShortKeyData(suffix: string, payload: string): void {
  if (typeof window === 'undefined') return;
  try {
    let raw = localStorage.getItem(SHORT_KEY_STORE_KEY);
    const store: Record<string, string> = raw ? JSON.parse(raw) : {};
    store[suffix] = payload;
    localStorage.setItem(SHORT_KEY_STORE_KEY, JSON.stringify(store));
  } catch { /* ignore */ }
}

function loadShortKeyData(suffix: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    let raw = localStorage.getItem(SHORT_KEY_STORE_KEY);
    if (!raw) return null;
    const store: Record<string, string> = JSON.parse(raw);
    return store[suffix] ?? null;
  } catch { return null; }
}

// ─── Generate a license key (self-contained encoded format) ──────────────────
export function generateLicenseKey(
  clientName: string,
  expiryDate: string,
  maxUploads: number,
  dailyLimit: number,
  modules: string[]
): string {
  const uid = Date.now().toString(36).toUpperCase().slice(-4);
  const modulesStr = modules.join(',');
  const parts = [clientName, expiryDate, String(maxUploads), String(dailyLimit), modulesStr, uid];
  const checksum = computeChecksum(parts);
  const payload = parts.join('~') + '~' + checksum;

  // Encode payload into a self-contained key — works on any device, no localStorage needed
  const encoded = encodePayload(payload);

  // Split into groups of 4 for readability: TURBO-XXXX-XXXX-...
  const chunks: string[] = [];
  for (let i = 0; i < encoded.length; i += 4) {
    chunks.push(encoded.slice(i, i + 4));
  }
  return 'TURBO-' + chunks.join('-');
}

// ─── Decode & validate a license key ─────────────────────────────────────────
export function validateLicenseKey(key: string): LicenseValidationResult {
  if (!key || typeof key !== 'string') {
    return { valid: false, error: 'No license key provided.' };
  }

  // Normalize
  const trimmed = key
    .trim()
    .replace(/\s+/g, '')
    .replace(/\u2014/g, '-')
    .replace(/\u2013/g, '-')
    .replace(/\uFB01/g, 'fi')
    .replace(/\uFB02/g, 'fl')
    .replace(/\uFB00/g, 'ff')
    .replace(/\uFB03/g, 'ffi')
    .replace(/\uFB04/g, 'ffl')
    .replace(/[\u200B-\u200D\uFEFF]/g, '');

  const upper = trimmed.toUpperCase();

  // Check blacklist
  if (BLACKLISTED_KEYS.includes(upper)) {
    return { valid: false, error: 'This license key has been revoked. Please contact support.' };
  }

  // ── New short format: TURBO-XXXXX (exactly 11 chars) ─────────────────────
  if (/^TURBO-[A-Z2-9]{5}$/.test(upper)) {
    const suffix = upper.replace('TURBO-', '');
    const payload = loadShortKeyData(suffix);
    if (!payload) {
      return { valid: false, error: 'License key not recognized. Please contact support.' };
    }

    const parts = payload.split('~');
    if (parts.length !== 7) {
      return { valid: false, error: 'Invalid license key structure.' };
    }

    const [clientName, expiryDate, maxUploadsStr, dailyLimitStr, modulesStr, uid, checksum] = parts;
    const dataParts = [clientName, expiryDate, maxUploadsStr, dailyLimitStr, modulesStr, uid];
    const expectedChecksum = computeChecksum(dataParts);
    if (checksum !== expectedChecksum) {
      return { valid: false, error: 'License key is tampered or invalid.' };
    }

    const today = new Date().toISOString().slice(0, 10);
    if (expiryDate < today) {
      return { valid: false, error: `License expired on ${expiryDate}. Please renew your license.` };
    }

    return {
      valid: true,
      data: {
        clientName,
        expiryDate,
        maxUploads: parseInt(maxUploadsStr, 10),
        dailyLimit: parseInt(dailyLimitStr, 10),
        modules: modulesStr.split(',').map(m => m.trim()).filter(Boolean),
        uid,
        rawKey: upper,
      },
    };
  }

  // ── Legacy long format: TURBO-XXXX-XXXX-... ──────────────────────────────
  if (upper.startsWith('TURBO-') && upper.length > 11) {
    const encoded = upper.replace(/^TURBO-/, '').replace(/-/g, '');
    const stripped = encoded.replace(/A+$/, '') || encoded;

    let raw: string;
    try {
      raw = decodePayload(stripped);
    } catch {
      return { valid: false, error: 'Invalid license key format.' };
    }

    const parts = raw.split('~');
    if (parts.length !== 7) {
      return { valid: false, error: 'Invalid license key structure.' };
    }

    const [clientName, expiryDate, maxUploadsStr, dailyLimitStr, modulesStr, uid, checksum] = parts;
    const dataParts = [clientName, expiryDate, maxUploadsStr, dailyLimitStr, modulesStr, uid];
    const expectedChecksum = computeChecksum(dataParts);
    if (checksum !== expectedChecksum) {
      return { valid: false, error: 'License key is tampered or invalid.' };
    }

    const today = new Date().toISOString().slice(0, 10);
    if (expiryDate < today) {
      return { valid: false, error: `License expired on ${expiryDate}. Please renew your license.` };
    }

    return {
      valid: true,
      data: {
        clientName,
        expiryDate,
        maxUploads: parseInt(maxUploadsStr, 10),
        dailyLimit: parseInt(dailyLimitStr, 10),
        modules: modulesStr.split(',').map(m => m.trim()).filter(Boolean),
        uid,
        rawKey: trimmed,
      },
    };
  }

  // ── Legacy base64 format ──────────────────────────────────────────────────
  let raw: string;
  try {
    const b64 = trimmed.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '=='.slice(0, (4 - (b64.length % 4)) % 4);
    raw = atob(padded);
  } catch {
    return { valid: false, error: 'Invalid license key format.' };
  }

  const parts = raw.split('~');
  if (parts.length !== 9 || parts[0] !== 'TSP') {
    return { valid: false, error: 'Invalid license key structure.' };
  }

  const [, clientName, expiryDate, maxUploadsStr, dailyLimitStr, modulesStr, uid, checksum] = parts;
  const dataParts = [clientName, expiryDate, maxUploadsStr, dailyLimitStr, modulesStr, uid];
  const expectedChecksum = computeChecksum(dataParts);
  if (checksum !== expectedChecksum) {
    return { valid: false, error: 'License key is tampered or invalid.' };
  }

  const today = new Date().toISOString().slice(0, 10);
  if (expiryDate < today) {
    return { valid: false, error: `License expired on ${expiryDate}. Please renew your license.` };
  }

  return {
    valid: true,
    data: {
      clientName,
      expiryDate,
      maxUploads: parseInt(maxUploadsStr, 10),
      dailyLimit: parseInt(dailyLimitStr, 10),
      modules: modulesStr.split(',').map(m => m.trim()).filter(Boolean),
      uid,
      rawKey: trimmed,
    },
  };
}

// ─── localStorage helpers ─────────────────────────────────────────────────────
const LS_KEY = 'tsp_license_key';
const LS_USAGE = 'tsp_usage';

export function getStoredLicense(): LicenseData | null {
  if (typeof window === 'undefined') return null;
  try {
    let raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    let result = validateLicenseKey(raw);
    if (result.valid && result.data) return result.data;
    localStorage.removeItem(LS_KEY);
    return null;
  } catch {
    return null;
  }
}

export function storeLicense(key: string): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(LS_KEY, key.trim());
}

export function clearLicense(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(LS_KEY);
  localStorage.removeItem(LS_USAGE);
}

// ─── Usage tracking ───────────────────────────────────────────────────────────
export function getUsage(): UsageData {
  if (typeof window === 'undefined') return { totalUploads: 0, dailyUploads: 0, lastUploadDate: '' };
  try {
    let raw = localStorage.getItem(LS_USAGE);
    if (!raw) return { totalUploads: 0, dailyUploads: 0, lastUploadDate: '' };
    return JSON.parse(raw) as UsageData;
  } catch {
    return { totalUploads: 0, dailyUploads: 0, lastUploadDate: '' };
  }
}

export function recordUpload(): void {
  if (typeof window === 'undefined') return;
  const today = new Date().toISOString().slice(0, 10);
  const usage = getUsage();
  const dailyUploads = usage.lastUploadDate === today ? usage.dailyUploads + 1 : 1;
  const updated: UsageData = {
    totalUploads: usage.totalUploads + 1,
    dailyUploads,
    lastUploadDate: today,
  };
  localStorage.setItem(LS_USAGE, JSON.stringify(updated));
}

export function checkUploadAllowed(license: LicenseData): { allowed: boolean; reason?: string } {
  const usage = getUsage();
  const today = new Date().toISOString().slice(0, 10);
  const dailyUploads = usage.lastUploadDate === today ? usage.dailyUploads : 0;

  if (license.maxUploads !== -1 && usage.totalUploads >= license.maxUploads) {
    return { allowed: false, reason: `Upload limit reached (${license.maxUploads} total uploads).` };
  }
  if (license.dailyLimit !== -1 && dailyUploads >= license.dailyLimit) {
    return { allowed: false, reason: `Daily upload limit reached (${license.dailyLimit}/day). Try again tomorrow.` };
  }
  return { allowed: true };
}

export function hasModuleAccess(license: LicenseData | null, moduleName: string): boolean {
  if (!license) return false;
  if (license.modules.includes('all')) return true;
  return license.modules.includes(moduleName.toLowerCase());
}
